// The Placeholder token grammar of TranslationTools/docs/adr/0002-placeholder-icu-token-syntax.md, ported from `PlaceholderTokenParser`.
// It MUST match the .NET / KMP impls.

export type Segment =
   | { kind: "literal"; text: string }
   | { kind: "token"; name: string };

/** `{` + a camelCase identifier + `}`; group 1 is the identifier. */
const TOKEN = /\{([a-z][a-zA-Z0-9]*)\}/;
const LEADING_TOKEN_RE = new RegExp("^" + TOKEN.source);
const WOULD_BE_TOKEN_RE = new RegExp(TOKEN.source, "g");

/**
 * Splits a raw value into ordered literal/token segments. A literal brace is written with the ICU
 * apostrophe escape (`'{'` / `'}'`). A direct port of `PlaceholderTokenParser.Parse`.
 */
export function parsePlaceholders(input: string | null | undefined): Segment[] {
   const segments: Segment[] = [];

   if (!input) {
      return segments;
   }

   let buffer = "";

   const flush = () => {
      if (buffer.length > 0) {
         segments.push({ kind: "literal", text: buffer });
         buffer = "";
      }
   };

   let i = 0;
   const length = input.length;

   while (i < length) {
      const c = input[i];

      if (c === "'") {
         // Doubled apostrophe -> literal apostrophe.
         if (i + 1 < length && input[i + 1] === "'") {
            buffer += "'";
            i += 2;
            continue;
         }

         // Apostrophe immediately before a brace opens a quoted span; braces inside are literal.
         if (i + 1 < length && (input[i + 1] === "{" || input[i + 1] === "}")) {
            i++; // consume opening apostrophe

            while (i < length) {
               if (input[i] === "'") {
                  if (i + 1 < length && input[i + 1] === "'") {
                     buffer += "'";
                     i += 2;
                     continue;
                  }

                  i++; // consume closing apostrophe
                  break;
               }

               buffer += input[i];
               i++;
            }

            continue;
         }

         // Lone apostrophe -> literal.
         buffer += "'";
         i++;
         continue;
      }

      if (c === "{") {
         const match = LEADING_TOKEN_RE.exec(input.slice(i));

         if (match) {
            flush();
            segments.push({ kind: "token", name: match[1] });
            i += match[0].length;
            continue;
         }

         // Not a valid token -> literal brace.
         buffer += "{";
         i++;
         continue;
      }

      // '}' and every other char.
      buffer += c;
      i++;
   }

   flush();
   return segments;
}

/**
 * Escapes ONLY would-be tokens in a literal text run: a `{camelCaseId}` that must stay literal is
 * apostrophe-escaped to `'{'id'}'` so the tokenizer re-reads it as literal. Every OTHER brace
 * (`{0}`, a lone `{`, `{PascalCase}`, `{ }`) is already inert under the grammar, so it is left
 * byte-stable. This is deliberate: rewriting a pre-existing `{0}` to `'{'0'}'` would make
 * `String.Format` throw on a client still running the pre-placeholder library (the management UI
 * ships to every project, not opt-in). Apostrophes are never touched.
 */
export function escapeLiteralBraces(text: string): string {
   return text.replace(WOULD_BE_TOKEN_RE, "'{'$1'}'");
}

/** The friendly chip label of a placeholder identifier: `userName` -> "User name". Mirrors `PlaceholderLabel.Derive`. */
export function deriveLabel(identifier: string): string {
   if (!identifier) {
      return "";
   }

   const words: string[] = [];
   let current = "";

   for (const ch of identifier) {
      if (ch >= "A" && ch <= "Z" && current.length > 0) {
         words.push(current);
         current = "";
      }

      current += ch;
   }

   if (current.length > 0) {
      words.push(current);
   }

   const lowered = words.map((w) => w.toLowerCase());

   if (lowered.length > 0 && lowered[0].length > 0) {
      lowered[0] = lowered[0][0].toUpperCase() + lowered[0].slice(1);
   }

   return lowered.join(" ");
}
